import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router';
import httpService from '../../services/httpService';
import { ApiEndpoints, ImportTemplates, Constants } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';
import DataManagementDataTable from '../DataManagement/DataManagementDataTable';
import { Column } from 'primereact/column';
import { createDataManagementQuery, useDataManagementPagination } from '../DataManagement/useDataManagementPagination';
import './SmaComponent.scss';
import SafeHtml from '../Common/SafeHtml';

interface ProviderTypeOption {
  label: string;
  value: string;
}

type ProviderFeatureState = boolean | 'loading' | 'error';

const resolveProviderType = (providerType: string | undefined, options: ProviderTypeOption[]) => {
  const value = providerType?.trim() || '';
  const key = value.toLowerCase();
  const option = options.find(option => option.value.trim().toLowerCase() === key)
    ?? options.find(option => option.label.trim().toLowerCase() === key);
  return option?.value.trim() || value;
};

const renderSmaActions = (
  standard: any,
  featureState: ProviderFeatureState | undefined,
  onView: (standard: any) => void,
  onDelete: (standard: any) => void,
) => {
  const canEdit = featureState === true;
  const pending = featureState === undefined || featureState === 'loading';
  const label = pending ? 'Checking measure permissions'
    : canEdit ? 'Edit measure details' : 'View measure details';

  return (
    <>
      <Button
        icon={pending ? 'pi pi-spinner pi-spin' : canEdit ? 'pi pi-pencil' : 'pi pi-eye'}
        className="p-button-text"
        aria-label={label}
        title={label}
        disabled={pending}
        style={{ marginRight: 8 }}
        onClick={() => onView(standard)}
      />
      <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => onDelete(standard)} />
    </>
  );
};

interface SmaComponentProps {
  providerTypes: ProviderTypeOption[];
}

const SmaComponent: React.FC<SmaComponentProps> = ({providerTypes}) => {
  const navigate = useNavigate();
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('sma-component-styles');
    return cleanup;
  }, []);

  const [tiers, setTiers] = useState<any[]>([]);
  const [measures, setMeasures] = useState<any[]>([]);
  const [allStandards, setAllStandards] = useState<any[]>([]);
  const [performanceAreas, setPerformanceAreas] = useState<any[]>([]);
  const [measureStatuses, setMeasureStatuses] = useState<any[]>([]);
  const [compositeGroups, setCompositeGroups] = useState<any[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);

  useEffect(() => {
    const lookupRequests = [
      httpService.get(ApiEndpoints.GetTiers)
      .then((r: any) => setTiers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTiers([])),
      httpService.get(ApiEndpoints.GetAllMeasures)
      .then((r: any) => setMeasures(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMeasures([])),
      httpService.get(ApiEndpoints.GetAllStandards)
      .then((r: any) => setAllStandards(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAllStandards([])),
      httpService.get(ApiEndpoints.GetAllPerformanceAreas)
      .then((r: any) => setPerformanceAreas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPerformanceAreas([])),
      httpService.get(ApiEndpoints.GetMeasureStatuses)
      .then((r: any) => setMeasureStatuses(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMeasureStatuses([])),
      httpService.get(ApiEndpoints.GetCompositeMeasureGroups)
      .then((r: any) => setCompositeGroups(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCompositeGroups([])),
    ];

    Promise.all(lookupRequests).finally(() => setLookupsLoading(false));
  }, []);

  const [providerFeatureEnabled, setProviderFeatureEnabled] = useState<Record<string, ProviderFeatureState>>({});
  const toast = useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();
  const tableViewportRef = useRef<HTMLDivElement>(null);
  const screenScrollbarRef = useRef<HTMLDivElement>(null);
  const [horizontalScrollMetrics, setHorizontalScrollMetrics] = useState({ contentWidth: 0, viewportWidth: 0, left: 0 });

  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.PERFORMANCE_STANDARDS);
      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.PERFORMANCE_STANDARDS,
      });
      await loadData();
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Performance standards uploaded successfully', life: 3000 });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Bulk upload failed';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.standardMeasureAssociation,
    onUpload: handleExcelUpload,
    title: 'Import Performance Standards from Excel',
    prominentDownloadLink: true,
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedStandard, setSelectedStandard] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [standardToDelete, setStandardToDelete] = useState<any>(null);

  const query = createDataManagementQuery({ providerType: providerTypeFilter, search });
  const {
    items: standards,
    totalRecords,
    page: currentPage,
    setPage: setCurrentPage,
    loading,
    setLoading,
    error,
    refresh,
  } = useDataManagementPagination<any>(ApiEndpoints.GetPagedPerformanceStandardsActive, query, {
    debounceMs: search ? 300 : 0,
    errorPrefix: 'Failed to fetch performance standards',
    preserveItemsOnError: true,
    recoverEmptyPageWhenNoResults: true,
  });

  const loadData = () => refresh();

  const tableStandards = useMemo(() => standards.map(standard => {
    const providerType = resolveProviderType(standard.providerType, providerTypes);
    return {
      ...standard,
      measurePermission: providerType ? providerFeatureEnabled[providerType] : 'error',
    };
  }), [standards, providerTypes, providerFeatureEnabled]);

  useEffect(() => {
    const tableViewport = tableViewportRef.current?.querySelector<HTMLElement>('.p-datatable-wrapper');
    const table = tableViewport?.querySelector<HTMLElement>('.p-datatable-table');
    if (!tableViewport || !table || typeof ResizeObserver === 'undefined') return;

    const updateMetrics = () => setHorizontalScrollMetrics({
      contentWidth: tableViewport.scrollWidth,
      viewportWidth: tableViewport.clientWidth,
      left: tableViewport.getBoundingClientRect().left,
    });
    const syncScreenScrollbar = () => {
      const screenScrollbar = screenScrollbarRef.current;
      if (screenScrollbar && screenScrollbar.scrollLeft !== tableViewport.scrollLeft)
        screenScrollbar.scrollLeft = tableViewport.scrollLeft;
    };
    const observer = new ResizeObserver(updateMetrics);
    observer.observe(tableViewport);
    observer.observe(table);
    tableViewport.addEventListener('scroll', syncScreenScrollbar);
    window.addEventListener('resize', updateMetrics);
    updateMetrics();

    return () => {
      observer.disconnect();
      tableViewport.removeEventListener('scroll', syncScreenScrollbar);
      window.removeEventListener('resize', updateMetrics);
    };
  }, [standards]);

  const syncTableHorizontalScroll = () => {
    const tableViewport = tableViewportRef.current?.querySelector<HTMLElement>('.p-datatable-wrapper');
    const screenScrollbar = screenScrollbarRef.current;
    if (tableViewport && screenScrollbar && tableViewport.scrollLeft !== screenScrollbar.scrollLeft)
      tableViewport.scrollLeft = screenScrollbar.scrollLeft;
  };

  useEffect(() => {
    const providerCodes = [...new Set(standards.map(standard => resolveProviderType(standard.providerType, providerTypes)).filter(Boolean))];
    let isCurrent = true;

    setProviderFeatureEnabled(Object.fromEntries(providerCodes.map(providerType => [providerType, 'loading' as const])));
    providerCodes.forEach(async providerType => {
      try {
        const response = await httpService.get<{ isEnabled?: unknown }>(
          `${ApiEndpoints.GetProviderFeature}/${encodeURIComponent(providerType)}/${Constants.MEASURE_LIBRARY_LOCKED}`
        );
        const isEnabled = response.data?.isEnabled;
        if (typeof isEnabled !== 'boolean') throw new Error('Invalid measure library feature response');
        if (isCurrent) setProviderFeatureEnabled(previous => ({ ...previous, [providerType]: isEnabled }));
      } catch {
        if (!isCurrent) return;
        setProviderFeatureEnabled(previous => ({ ...previous, [providerType]: 'error' }));
        toast.current?.show({
          severity: 'error',
          summary: 'Permission check failed',
          detail: `Unable to check measure permissions for ${providerType}. Refresh to retry.`,
          life: 6000,
        });
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [standards, providerTypes]);

  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes : Array.from(
    new Set(standards.map(s => s.providerType).filter(Boolean))
  ).map(pt => ({ label: pt, value: pt }));

  const navigateToMeasureDetails = (standard: any) => {
    const providerType = resolveProviderType(standard.providerType, providerTypes);
    navigate(`/measureDetails/${standard.standardMeasureId}?providerType=${encodeURIComponent(providerType)}`, {
      state: {
        performanceAreaTitle: standard.performanceAreaTitle,
      },
    });
  };

  const handleSave = async () => {
    if (!selectedStandard) return;
    const validationErrors = getFormValidationErrors(dataManagementFormConfig.measureMapping, selectedStandard);
    if (validationErrors.length > 0) {
      toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
      return;
    }
    const payload = normalizeFieldValues(dataManagementFormConfig.measureMapping, selectedStandard);
    try {
      setLoading(true);
      if (isAddMode) {
        await httpService.post(ApiEndpoints.AddPerformanceStandard, payload);
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Performance standard added successfully', life: 3000 });
      } else {
        await httpService.put(`${ApiEndpoints.UpdatePerformanceStandard}/${selectedStandard.standardMeasureId}`, { ...payload, isActive: selectedStandard.isActive });
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Performance standard updated successfully', life: 3000 });
      }
      setEditDialogVisible(false);
      setIsAddMode(false);
      await loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Save failed';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
      setTimeout(() => {
        setEditDialogVisible(false);
        setIsAddMode(false);
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!standardToDelete) return;
    try {
      setLoading(true);
      await httpService.delete(`${ApiEndpoints.DeletePerformanceStandard}/${standardToDelete.standardMeasureId}`);
      await loadData();
      setDeleteDialogVisible(false);
      setStandardToDelete(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete performance standard';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="measure-mapping-screen" style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading || lookupsLoading}>
      <DataManagementLoader visible={loading || lookupsLoading} label="Loading performance standards" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New" icon="pi pi-plus" onClick={() => {
            setSelectedStandard({
              standardMeasureId: 0,
              performanceAreaTitle: '',
              tierName: '',
              standardTitle: '',
              measureNumber: '',
              measureTitle: '',
              measureDescription: '',
              scoringCriteria: '',
              measureStatusName: '',
              compositeGroupName: '',
              providerType: '',
              reviewYear: '',
              reviewConfigId: null,
            });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="sma-search-anim">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>

      {horizontalScrollMetrics.contentWidth > horizontalScrollMetrics.viewportWidth && (
        <div
          ref={screenScrollbarRef}
          className="measure-mapping-screen-horizontal-scrollbar"
          onScroll={syncTableHorizontalScroll}
          style={{ left: horizontalScrollMetrics.left, width: horizontalScrollMetrics.viewportWidth }}
          tabIndex={0}
          aria-label="Scroll Measure Mapping table horizontally"
        >
          <div style={{ width: horizontalScrollMetrics.contentWidth, height: 1 }} />
        </div>
      )}
      <div ref={tableViewportRef} className="measure-mapping-table-viewport">
        <DataManagementDataTable value={tableStandards} dataKey="standardMeasureId" page={currentPage} totalRecords={totalRecords}
        onPage={setCurrentPage} loading={loading} emptyMessage="No performance standards found."
          scrollable tableStyle={{ minWidth: '100%', background: '#fff' }}>
        <Column header="#" body={(_standard: any, options) => options.rowIndex + 1} headerStyle={{ width: 64, minWidth: 40 }} />
        <Column field="tierName" header="Tier Name" body={(standard: any) => standard.tierName || '-'} />
        <Column field="performanceAreaTitle" header="Performance Area Title" body={(standard: any) => standard.performanceAreaTitle || '-'} />
        <Column field="standardTitle" header="Standard Title" body={(standard: any) => standard.standardTitle || '-'} />
        <Column field="measureNumber" header="Measure Number" body={(standard: any) => standard.measureNumber || '-'} />
        <Column field="measureTitle" header="Measure Title" headerStyle={{ width: '320px', minWidth: '220px' }} bodyStyle={{ minWidth: '220px' }} bodyClassName="data-management-wrap-cell" body={(standard: any) => standard.measureTitle || '-'} />
        <Column field="measureDescriptionHtml" header="Measure Description" headerStyle={{ width: '360px', minWidth: '260px' }} bodyStyle={{ minWidth: '260px' }} bodyClassName="data-management-description-cell" body={(standard: any) => <SafeHtml html={standard.measureDescriptionHtml ?? '-'} />} />
        <Column field="scoringCriteria" header="Scoring Criteria" headerStyle={{ width: '520px', minWidth: '850px' }} bodyStyle={{ minWidth: '400px' }} bodyClassName="data-management-description-cell" body={(standard: any) => <SafeHtml html={standard.scoringCriteria ?? '-'} />} />
        <Column field="measureStatusName" header="Measure Status" body={(standard: any) => standard.measureStatusName || '-'} />
        <Column field="compositeGroupName" header="Composite Group" body={(standard: any) => standard.compositeGroupName || '-'} />
        <Column field="providerType" header="Provider Type" body={(standard: any) => standard.providerType || '-'} />
        <Column field="reviewYear" header="Review Year" body={(standard: any) => standard.reviewYear || '-'} />
        <Column header="Actions" body={(standard: any) => {
          return renderSmaActions(
            standard,
            standard.measurePermission,
            navigateToMeasureDetails,
            currentStandard => { setStandardToDelete(currentStandard); setDeleteDialogVisible(true); },
          );
        }} />
        </DataManagementDataTable>
      </div>

      <Dialog
        header={isAddMode ? 'Add Performance Standard' : `Edit Performance Standard${selectedStandard?.measureNumber ? ` - ${selectedStandard.measureNumber}` : ''}`}
        visible={editDialogVisible}
        style={{ width: '700px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={handleSave} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <SmaEditForm
          editStandard={selectedStandard}
          setEditStandard={setSelectedStandard}
          tiers={tiers}
          measures={measures}
          allStandards={allStandards}
          performanceAreas={performanceAreas}
          measureStatuses={measureStatuses}
          compositeGroups={compositeGroups}
          providerTypesOptions={providerTypeOptions}
        />
      </Dialog>

      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={handleDelete} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{standardToDelete?.measureNumber ? `: ${standardToDelete.measureNumber}` : ''}?
        </div>
      </Dialog>
    </div>
  );
};

interface SmaEditFormProps {
  editStandard: any;
  setEditStandard: React.Dispatch<React.SetStateAction<any>>;
  tiers: any[];
  measures: any[];
  allStandards: any[];
  performanceAreas: any[];
  measureStatuses: any[];
  compositeGroups: any[];
  providerTypesOptions: { label: string; value: string }[];
}

const SmaEditForm: React.FC<SmaEditFormProps> = ({ editStandard, setEditStandard, tiers, measures, allStandards, performanceAreas, measureStatuses, compositeGroups, providerTypesOptions }) => {
  const [reviewYears, setReviewYears] = useState<any[]>([]);

  useEffect(() => {
    const providerType = editStandard?.providerType;
    if (!providerType) {
      setReviewYears([]);
      return;
    }

    let isCurrent = true;
    httpService.get(`${ApiEndpoints.GetAllActiveReviewYearDetails}/${encodeURIComponent(providerType)}`)
      .then((response: any) => {
        if (isCurrent) setReviewYears(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (isCurrent) setReviewYears([]);
      });

    return () => {
      isCurrent = false;
    };
  }, [editStandard?.providerType]);

  React.useEffect(() => {
    if (!editStandard) return;

    const resolvedNames: Record<string, string> = {};
    const resolveName = (idField: string, nameField: string, list: any[], idKey: string, nameKey: string) => {
      if (editStandard[idField] && !editStandard[nameField]) {
        const item = list.find(option => String(option[idKey]) === String(editStandard[idField]));
        if (item) resolvedNames[nameField] = item[nameKey];
      }
    };

    resolveName('tierId', 'tierName', tiers, 'tierId', 'tierName');
    resolveName('standardId', 'standardTitle', allStandards, 'standardId', 'title');
    resolveName('measureId', 'measureNumber', measures, 'measureId', 'measureNumber');
    resolveName('performanceAreaId', 'performanceAreaTitle', performanceAreas, 'performanceAreaId', 'performanceAreaTitle');
    resolveName('measureStatusId', 'measureStatusName', measureStatuses, 'measureStatusId', 'measureStatus');
    resolveName('compositeGroupId', 'compositeGroupName', compositeGroups, 'compositeGroupId', 'groupName');

    if (Object.keys(resolvedNames).length > 0) {
      setEditStandard({ ...editStandard, ...resolvedNames });
    }
  }, [editStandard, tiers, allStandards, measures, performanceAreas, measureStatuses, compositeGroups, setEditStandard]);

  const filteredPerformanceAreas = performanceAreas.filter(pa =>
    ((!editStandard?.providerType || pa.providerType === editStandard.providerType)) ||
    String(pa.performanceAreaId) === String(editStandard?.performanceAreaId) ||
    pa.performanceAreaTitle === editStandard?.performanceAreaTitle
  );
  const filteredTiers = tiers.filter(t =>
    ((!editStandard?.providerType || t.providerType === editStandard.providerType)) ||
    String(t.tierId) === String(editStandard?.tierId) ||
    t.tierName === editStandard?.tierName
  );
  const filteredStandards = allStandards.filter(s =>
    ((!editStandard?.providerType || s.providerType === editStandard.providerType) &&  (!editStandard?.performanceAreaId ||  s.performanceAreaId == editStandard.performanceAreaId)) ||
    String(s.standardId) === String(editStandard?.standardId) ||
    s.title === editStandard?.standardTitle
  );
  const filteredMeasures = measures.filter(m =>
    ((!editStandard?.providerType || m.providerType === editStandard.providerType)) ||
    String(m.measureId) === String(editStandard?.measureId) ||
    m.measureNumber === editStandard?.measureNumber
  );

  return (
    <DataManagementForm
      fields={dataManagementFormConfig.measureMapping}
      values={editStandard}
      onChange={setEditStandard}
      options={{
        providerType: providerTypesOptions.map(option => ({ ...option, id: option.value })),
        reviewYear: reviewYears.map(reviewYear => ({
          label: String(reviewYear.reviewYear ?? reviewYear.ReviewYear ?? ''),
          value: reviewYear.reviewYear ?? reviewYear.ReviewYear,
          id: reviewYear.reviewConfigId ?? reviewYear.reviewConfigurationId ?? reviewYear.ReviewConfigurationId,
        })),
        performanceAreaTitle: filteredPerformanceAreas.map(area => ({ label: area.performanceAreaTitle, value: area.performanceAreaTitle, id: area.performanceAreaId })),
        tierName: filteredTiers.map(tier => ({ label: tier.tierName, value: tier.tierName, id: tier.tierId })),
        standardTitle: filteredStandards.map(standard => ({ label: standard.title, value: standard.title, id: standard.standardId })),
        measureNumber: filteredMeasures.map(measure => ({ label: measure.measureNumber, value: measure.measureNumber, id: measure.measureId })),
        measureStatusName: measureStatuses.map(status => ({ label: status.measureStatus, value: status.measureStatus, id: status.measureStatusId })),
        compositeGroupName: compositeGroups.map(group => ({ label: group.groupName, value: group.groupName, id: group.compositeGroupId })),
      }}
    />
  );
};

export default SmaComponent;
